/**
 * COMMERCE-176B-R1/R2 — canonical customer-facing order lifecycle projection.
 * Single mapping for list / detail / success / messages — do not fork copy ad hoc.
 * TTN_ASSIGNED ≠ SHIPPED (provider evidence required).
 */
import type { StoreV2Lang } from "./store-v2-locale.js";

export type CanonicalCustomerOrderStage =
  | "NEW"
  | "ACCEPTED"
  | "PREPARING"
  | "READY_FOR_SHIPMENT"
  | "TTN_ASSIGNED"
  | "SHIPPED"
  | "IN_TRANSIT"
  | "READY_FOR_PICKUP"
  | "DELIVERED"
  | "COMPLETED"
  | "CANCELLED";

export function mapToCanonicalCustomerStage(input: {
  order_top_status?: string | null | undefined;
  status?: string | null | undefined;
  primary_order_state?: string | null | undefined;
  ttn_status?: string | null | undefined;
  delivery_status?: string | null | undefined;
  ttn_number?: string | null | undefined;
}): CanonicalCustomerOrderStage {
  const top = String(input.order_top_status ?? input.primary_order_state ?? input.status ?? "")
    .trim()
    .toLowerCase();
  const ttn = String(input.ttn_status ?? "").toLowerCase();
  const del = String(input.delivery_status ?? "").toLowerCase();
  const hasTtnDigits = String(input.ttn_number ?? "").replace(/\D/g, "").length === 14;

  if (top === "canceled" || top === "cancelled") return "CANCELLED";
  if (top === "completed") return "COMPLETED";
  if (
    del.includes("delivered") ||
    del.includes("picked") ||
    ttn.includes("delivered") ||
    ttn.includes("received")
  ) {
    return "DELIVERED";
  }
  if (
    ttn.includes("ready_for_pickup") ||
    ttn.includes("arrived_warehouse") ||
    ttn.includes("arrived_at_branch") ||
    del.includes("ready_for_pickup")
  ) {
    return "READY_FOR_PICKUP";
  }
  if (ttn.includes("in_transit") || del.includes("in_transit")) return "IN_TRANSIT";
  if (ttn.includes("handed") || ttn.includes("carrier_accepted") || ttn === "created_live") {
    return "SHIPPED";
  }
  // COMMERCE-176B-R2 — assigned TTN alone is NOT shipped.
  if (
    ttn === "ttn_assigned" ||
    ttn === "attached" ||
    ttn === "ttn_created" ||
    ttn === "has_ttn" ||
    (hasTtnDigits && !/not_live|pending|no_ttn|none/.test(ttn || "not_live"))
  ) {
    if (
      !ttn.includes("transit") &&
      !ttn.includes("handed") &&
      !ttn.includes("delivered") &&
      !ttn.includes("ready_for_pickup")
    ) {
      return "TTN_ASSIGNED";
    }
  }
  if (top === "shipped") return "SHIPPED";
  if (top === "ready_to_ship" || top === "ready" || top === "ready_for_shipment") {
    return "READY_FOR_SHIPMENT";
  }
  if (top === "preparing" || top === "packing") return "PREPARING";
  if (top === "accepted") return "ACCEPTED";
  if (top === "new" || top === "placed" || top === "payment_pending" || !top) return "NEW";
  if (top.includes("accept")) return "ACCEPTED";
  return "NEW";
}

export function canonicalOrderTitle(lang: StoreV2Lang, stage: CanonicalCustomerOrderStage): string {
  const en = lang === "en";
  switch (stage) {
    case "ACCEPTED":
      return en ? "Order accepted" : "Замовлення прийнято";
    case "PREPARING":
      return en ? "Preparing your order" : "Готуємо замовлення";
    case "READY_FOR_SHIPMENT":
      return en ? "Ready for shipment" : "Готове до відправлення";
    case "TTN_ASSIGNED":
      return en ? "Tracking number added" : "ТТН створено";
    case "SHIPPED":
      return en ? "Handed to Nova Poshta" : "Посилку передано Новій пошті";
    case "IN_TRANSIT":
      return en ? "On the way" : "Замовлення вже в дорозі";
    case "READY_FOR_PICKUP":
      return en ? "Ready for pickup" : "Посилка прибула та готова до отримання";
    case "DELIVERED":
      return en ? "Delivered" : "Замовлення отримано";
    case "COMPLETED":
      return en ? "Order completed" : "Замовлення отримано";
    case "CANCELLED":
      return en ? "Cancelled" : "Скасовано";
    case "NEW":
    default:
      return en ? "Awaiting store confirmation" : "Очікує підтвердження магазину";
  }
}

export function canonicalOrderExplanation(
  lang: StoreV2Lang,
  stage: CanonicalCustomerOrderStage,
): string | null {
  const en = lang === "en";
  switch (stage) {
    case "ACCEPTED":
      return en
        ? "The shop is preparing your order for shipment."
        : "Магазин готує замовлення до відправлення.";
    case "PREPARING":
      return en ? "Your order is being packed." : "Ваше замовлення комплектується.";
    case "TTN_ASSIGNED":
      return en
        ? "We are preparing the parcel for handover to Nova Poshta."
        : "Готуємо посилку до передачі Новій пошті.";
    case "SHIPPED":
      return en
        ? "The parcel was handed to Nova Poshta."
        : "Посилку передано Новій пошті.";
    case "IN_TRANSIT":
      return en ? "Your order is on the way." : "Замовлення вже в дорозі.";
    case "READY_FOR_PICKUP":
      return en
        ? "The parcel has arrived and is ready for pickup."
        : "Посилка прибула та готова до отримання.";
    case "DELIVERED":
      return en ? "Order received." : "Замовлення отримано.";
    case "NEW":
      return en
        ? "The shop will confirm your order shortly."
        : "Магазин незабаром підтвердить замовлення.";
    default:
      return null;
  }
}

export function isCodPaymentMethod(method: string | null | undefined): boolean {
  const m = String(method ?? "").toLowerCase();
  return (
    m.includes("cod") ||
    m.includes("cash_on_delivery") ||
    m === "nova_poshta_cod" ||
    m.includes("післяплат")
  );
}

/** Customer payment line for COD — never generic "awaiting online payment". */
export function codPaymentCustomerLabel(
  lang: StoreV2Lang,
  opts?: { hasTtn?: boolean; settled?: boolean },
): string {
  const en = lang === "en";
  if (opts?.settled) return en ? "Paid" : "Оплачено";
  if (opts?.hasTtn) {
    return en
      ? "Pay on receipt at Nova Poshta"
      : "Сплатите при отриманні у Новій пошті";
  }
  return en ? "Cash on delivery — pay on receipt…" : "Післяплата — оплата при отриманні…";
}
